<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters } from 'dashboard/composables/store';
import { useLoadWithRetry } from 'dashboard/composables/loadWithRetry';
import BaseBubble from './Base.vue';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import { downloadFile } from '@chatwoot/utils';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const { t } = useI18n();

const { filteredCurrentChatAttachments, attachments, conversationId } = useMessageContext();
const getters = useStoreGetters();

const attachment = computed(() => {
  return attachments.value[0];
});

const { isLoaded, hasError, loadWithRetry } = useLoadWithRetry();

const showGallery = ref(false);
const isDownloading = ref(false);
const isProcessingOCR = ref(false);
const showTranscriptBox = ref(true);
const isTranscriptExpanded = ref(false);
const isCopied = ref(false);
const localTranscribedText = ref('');
const TRANSCRIPT_PREVIEW_LENGTH = 350;

const activeTranscriptText = computed(() => {
  return localTranscribedText.value || attachment.value?.transcribedText || '';
});

const hasTranscript = computed(() => Boolean(activeTranscriptText.value && activeTranscriptText.value.trim()));

const isTranscriptLong = computed(
  () => activeTranscriptText.value.length > TRANSCRIPT_PREVIEW_LENGTH
);

const displayedTranscript = computed(() => {
  const text = activeTranscriptText.value;
  if (!isTranscriptLong.value || isTranscriptExpanded.value) return text;
  return `${text.slice(0, TRANSCRIPT_PREVIEW_LENGTH).trimEnd()}…`;
});

const formattedTranscript = computed(() => {
  if (!displayedTranscript.value) return '';
  return new MessageFormatter(displayedTranscript.value).formattedMessage;
});

onMounted(() => {
  if (attachment.value?.dataUrl) {
    loadWithRetry(attachment.value.dataUrl);
  }
});

const downloadAttachment = async () => {
  const { fileType, dataUrl, extension } = attachment.value;
  try {
    isDownloading.value = true;
    await downloadFile({ url: dataUrl, type: fileType, extension });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};

const handleImageError = () => {
  hasError.value = true;
};

const toggleOrTranscribe = async () => {
  if (hasTranscript.value) {
    showTranscriptBox.value = !showTranscriptBox.value;
    return;
  }

  const { dataUrl } = attachment.value;
  const accountId = getters.getCurrentAccountId.value;
  const convId = conversationId.value;

  try {
    isProcessingOCR.value = true;
    useAlert("⚙️ Solicitando análise IA (GPT-4o Vision)...");
    
    const res = await window.axios.post(`/api/v1/accounts/${accountId}/conversations/${convId}/ocr`, {
      attachment_url: dataUrl,
      attachment_id: attachment.value.id,
    });
    
    if (res.data && res.data.status === 'success') {
      if (res.data.text) {
        localTranscribedText.value = res.data.text;
      }
      showTranscriptBox.value = true;
      useAlert("✅ Imagem analisada com sucesso!");
    } else {
      useAlert("❌ Erro ao analisar imagem.");
    }
  } catch (err) {
    useAlert("❌ Falha na conexão com o servidor OCR.");
    console.error("OCR Error:", err);
  } finally {
    isProcessingOCR.value = false;
  }
};

const copyTranscript = async () => {
  if (!activeTranscriptText.value) return;
  try {
    await navigator.clipboard.writeText(activeTranscriptText.value);
    isCopied.value = true;
    setTimeout(() => {
      isCopied.value = false;
    }, 2000);
  } catch (e) {
    useAlert('Erro ao copiar texto.');
  }
};
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-2.5 min-w-72"
    data-bubble-name="image"
  >
    <div v-if="hasError" class="flex items-center gap-1 text-center rounded-lg p-3">
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      <p class="mb-0 text-n-slate-11">
        {{ $t('COMPONENTS.MEDIA.IMAGE_UNAVAILABLE') }}
      </p>
    </div>
    <div v-else-if="isLoaded" class="relative group rounded-lg overflow-hidden cursor-pointer" @click="showGallery = true">
      <img
        class="skip-context-menu rounded-lg w-full object-cover"
        :src="attachment.dataUrl"
        :width="attachment.width"
        :height="attachment.height"
      />
      <div
        class="inset-0 p-2 pointer-events-none absolute bg-gradient-to-tl from-n-slate-12/30 dark:from-n-slate-1/50 via-transparent to-transparent hidden group-hover:flex"
      />
      <div class="absolute right-2 bottom-2 hidden group-hover:flex gap-1.5">
        <Button xs solid slate icon="i-lucide-expand" class="opacity-80" />
        <Button
          xs
          solid
          slate
          icon="i-lucide-download"
          class="opacity-80"
          :is-loading="isDownloading"
          :disabled="isDownloading"
          @click.stop="downloadAttachment"
        />
        <!-- Botão de Análise de Imagem sob Demanda com IA -->
        <Button
          xs
          solid
          slate
          :icon="isProcessingOCR ? 'i-lucide-loader-2' : hasTranscript ? 'i-lucide-file-text' : 'i-lucide-sparkles'"
          :class="[
            hasTranscript
              ? 'text-woot-600 dark:text-woot-400 bg-woot-50 dark:bg-woot-900/30'
              : 'opacity-90 hover:opacity-100'
          ]"
          :is-loading="isProcessingOCR"
          :disabled="isProcessingOCR"
          :title="
            isProcessingOCR
              ? 'Analisando imagem...'
              : hasTranscript
              ? 'Ver / Ocultar transcrição'
              : 'Analisar imagem com IA'
          "
          @click.stop="toggleOrTranscribe"
        />
      </div>
    </div>

    <!-- Texto de Transcrição Integrado Diretamente no Corpo da Mensagem (Sem Caixa Separada) -->
    <div
      v-if="hasTranscript && showTranscriptBox"
      class="mt-2.5 flex flex-col gap-2 w-full"
    >
      <div class="flex items-center justify-between">
        <span class="text-[12px] font-semibold text-woot-600 dark:text-woot-400 flex items-center gap-1.5">
          <Icon icon="i-lucide-sparkles" class="size-3.5" />
          <span>Transcrição da Imagem</span>
          <span class="opacity-60 text-[10px] font-normal text-n-slate-11">· Generated by Zéti Desk</span>
        </span>
        <button
          class="text-[11px] text-n-slate-11 hover:text-n-slate-12 flex items-center gap-1 bg-n-alpha-2 hover:bg-n-alpha-3 px-2 py-0.5 rounded-md transition-colors cursor-pointer border-0"
          :title="isCopied ? 'Copiado!' : 'Copiar transcrição'"
          @click.stop="copyTranscript"
        >
          <Icon :icon="isCopied ? 'i-lucide-check' : 'i-lucide-copy'" class="size-3 text-woot-500" />
          <span>{{ isCopied ? 'Copiado!' : 'Copiar' }}</span>
        </button>
      </div>

      <div class="text-xs leading-relaxed text-n-slate-12 font-normal">
        <div v-dompurify-html="formattedTranscript" class="prose prose-bubble text-xs [&>p]:mb-1.5 [&>ul]:pl-3 [&>h2]:text-xs [&>h2]:font-bold [&>h3]:text-xs [&>h3]:font-bold" />
      </div>

      <button
        v-if="isTranscriptLong"
        class="self-start p-0 border-0 bg-transparent text-[11px] text-woot-600 dark:text-woot-400 hover:underline font-medium cursor-pointer"
        @click.stop="isTranscriptExpanded = !isTranscriptExpanded"
      >
        {{ isTranscriptExpanded ? 'Mostrar menos' : 'Mostrar mais' }}
      </button>
    </div>

    <GalleryView
      v-if="showGallery"
      v-model:show="showGallery"
      :attachment="useSnakeCase(attachment)"
      :all-attachments="filteredCurrentChatAttachments"
      @error="handleImageError"
      @close="() => (showGallery = false)"
    />
  </BaseBubble>
</template>
